import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Link,
  CircularProgress
} from '@material-ui/core';
import {
  SaveOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline
} from '@material-ui/icons';
import validate from 'validate.js';
import { connect } from 'react-redux';
import {
  getSubjectiveTemplates,
  UpdateSubjectiveTemplate,
  DeleteSubjectiveTemplate,
  AddSubjectiveTemplate
} from 'services/providers/action';

const schema = {
  topic: {
    presence: { allowEmpty: false }
  }
};

const initialUserState = {
  id: null,
  referencedata: false,
  topic: '',
  topicText: [],
  isValid: false,
  touched: {},
  errors: {}
};

const MyTemplates = props => {
  const { classes, ehrID, userID } = props;

  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState(initialUserState);
  const [subjectiveTopics, setSubjectiveTopics] = useState([]);
  const [editingIds, setEditingIds] = useState({}); // { [id]: true } — cleaner than array

  useEffect(() => {
    if (props.templates) {
      setSubjectiveTopics(props.templates);
    }
    setLoading(false);
  }, [props.templates]);

  // Refresh list from server
  const refreshTopics = useCallback(async () => {
    const res = await props.getSubjectiveTemplates(ehrID, userID);
    if (res?.data?.length > 0) {
      setSubjectiveTopics(res.data);
    }
  }, [ehrID, userID]);

  const handleTopicChange = (field, value) => {
    const newState = { ...values, [field]: value, touched: { ...values.touched, [field]: true } };
    const errors = validate(newState, schema);
    newState.errors = errors || {};
    newState.isValid = !errors;
    setValues(newState);
  };

  // Inline edit: update topic text in local state only (no API call yet)
  const handleInlineChange = (value, id) => {
    setSubjectiveTopics(prev =>
      prev.map(t => t.id === id ? { ...t, topic: value } : t)
    );
  };

  const startEdit = id => {
    setEditingIds(prev => ({ ...prev, [id]: true }));
  };

  const cancelEdit = useCallback(async () => {
    // Refresh to restore original values for any cancelled edit
    await refreshTopics();
    setEditingIds({});
  }, [refreshTopics]);

  const saveEdit = async subjective => {
    await props.UpdateSubjectiveTemplate(ehrID, userID, subjective);
    setEditingIds(prev => ({ ...prev, [subjective.id]: false }));
    await refreshTopics();
  };

  const deleteSubjectiveTopic = async id => {
    await props.DeleteSubjectiveTemplate(ehrID, userID, id);
    await refreshTopics();
  };

  const addTopic = async () => {
    if (!values.isValid) return;
    const data = {
      id: values.id,
      referencedata: values.referencedata,
      topic: values.topic,
      topicText: values.topicText
    };
    await props.AddSubjectiveTemplate(ehrID, userID, data);
    setValues(initialUserState);
    await refreshTopics();
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className={classes.templateWrap}>
        <div className={classes.templateHead}>
          <Typography>My Templates</Typography>
        </div>

        <div className={classes.templateBody}>
          <PerfectScrollbar style={{ flex: 1 }}>
            <div style={{ height: 400 }}>
              <Table className={classes.templateTable}>

                {/* Add new topic row — always visible at top */}
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '100%' }}>
                      <TextField
                        placeholder="Add New Topic"
                        variant="outlined"
                        className={classes.textBox}
                        InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                        value={values.topic}
                        onChange={e => handleTopicChange('topic', e.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center" style={{ whiteSpace: 'nowrap', maxWidth: 100 }}>
                      <IconButton
                        disabled={!values.isValid}
                        size="small"
                        color="primary"
                        onClick={addTopic}
                      >
                        <SaveOutlined />
                      </IconButton>
                      <IconButton size="small" onClick={() => setValues(initialUserState)}>
                        <ClearOutlined style={{ color: '#DE5555' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Template rows */}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" style={{ padding: 24 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : subjectiveTopics.length > 0 ? (
                    subjectiveTopics.map(subjective => (
                      <TableRow key={subjective.id}>
                        {editingIds[subjective.id] ? (
                          <>
                            <TableCell>
                              <TextField
                                placeholder="Edit Topic"
                                variant="outlined"
                                className={classes.textBox}
                                InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                                value={subjective.topic}
                                onChange={e => handleInlineChange(e.target.value, subjective.id)}
                              />
                            </TableCell>
                            <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                              <IconButton
                                disabled={subjective.topic === ''}
                                size="small"
                                color="primary"
                                onClick={() => saveEdit(subjective)}
                              >
                                <SaveOutlined />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={cancelEdit}
                              >
                                <ClearOutlined style={{ color: '#DE5555' }} />
                              </IconButton>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className={classes.letterFont}>
                              <Link
                                component="button"
                                onClick={() => props.setTopicText(subjective.topic)}
                              >
                                {subjective.topic}
                              </Link>
                            </TableCell>
                            <TableCell width={72} align="center">
                              <IconButton
                                size="small"
                                onClick={() => startEdit(subjective.id)}
                              >
                                <Edit color="primary" />
                              </IconButton>
                              <IconButton
                                size="small"
                                style={{
                                  visibility: subjective.topicText.length === 0 ? 'visible' : 'hidden'
                                }}
                                onClick={() => deleteSubjectiveTopic(subjective.id)}
                              >
                                <DeleteOutline style={{ color: '#DE5555' }} />
                              </IconButton>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        style={{ textAlign: 'center', color: 'rgba(0,0,0,0.4)', fontStyle: 'italic' }}
                      >
                        No records of subjective templates
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

              </Table>
            </div>
          </PerfectScrollbar>
        </div>
      </div>

      {/* Footer */}
      <Box className={classes.modalFooter}>
        <Button
          color="primary"
          onClick={props.onCancel}
          size="small"
          style={{ borderRadius: 4 }}
          variant="contained"
        >
          Close
        </Button>
      </Box>
    </Box>
  );
};

MyTemplates.propTypes = {
  className: PropTypes.string
};

const mapDispatchToProps = {
  getSubjectiveTemplates,
  UpdateSubjectiveTemplate,
  DeleteSubjectiveTemplate,
  AddSubjectiveTemplate
};

const mapStateToProps = state => ({
  ehrID: state.profile?.ehrID,
  userID: state.profile?.userID
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(MyTemplates))
);